import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import logoAsset from "@/assets/logo-pajar-benito-224.webp.asset.json";

const nav = [
  { to: "/", label: "Inicio" },
  { to: "/alojamiento", label: "Alojamiento" },
  { to: "/tarifas", label: "Tarifas" },
  { to: "/entorno", label: "Entorno" },
  { to: "/galeria", label: "Galería" },
  { to: "/blog", label: "Blog" },
  { to: "/contacto", label: "Contacto" },
] as const;

const navLeft = nav.slice(0, 3);
const navRight = nav.slice(3);

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    // Histéresis: distintos umbrales para entrar y salir del modo compacto,
    // así evitamos el bucle por el cambio de altura del header al hacer scroll.
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled((prev) => (prev ? y > 40 : y > 180));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background md:bg-background/85 md:backdrop-blur">
      {/* ===== Desktop / tablet ===== */}
      <div className="mx-auto hidden max-w-6xl px-4 md:block md:px-6">
        {scrolled ? (
          /* Compact: logo izquierda + menú derecha en una sola fila */
          <div className="flex items-center justify-between py-2 transition-all duration-300">
            <Link to="/" className="flex items-center gap-3" aria-label="El Pajar de Benito · Inicio">
              <img
                src={logoAsset.url}
                alt="El Pajar de Benito · Casa Rural"
                width={64}
                height={64}
                className="h-16 w-16 object-contain transition-all duration-300"
              />
            </Link>
            <nav className="flex items-center gap-5">
              {nav.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="text-sm text-foreground/80 transition-colors hover:text-primary"
                  activeProps={{ className: "text-primary" }}
                >
                  {n.label}
                </Link>
              ))}
              <Link
                to="/reserva"
                className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Reservar
              </Link>
            </nav>
          </div>
        ) : (
          /* Expanded: logo en el centro, menú repartido a los lados */
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-6 py-2 transition-all duration-300">
            <nav className="flex items-center justify-end gap-5">
              {navLeft.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="text-sm text-foreground/80 transition-colors hover:text-primary"
                  activeProps={{ className: "text-primary" }}
                >
                  {n.label}
                </Link>
              ))}
            </nav>
            <Link
              to="/"
              className="flex justify-center"
              aria-label="El Pajar de Benito · Inicio"
            >
              <img
                src={logoAsset.url}
                alt="El Pajar de Benito · Casa Rural"
                width={160}
                height={160}
                className="h-40 w-40 object-contain transition-all duration-300"
              />
            </Link>
            <nav className="flex items-center justify-start gap-5">
              {navRight.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="text-sm text-foreground/80 transition-colors hover:text-primary"
                  activeProps={{ className: "text-primary" }}
                >
                  {n.label}
                </Link>
              ))}
              <Link
                to="/reserva"
                className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Reservar
              </Link>
            </nav>
          </div>
        )}
      </div>

      {/* ===== Mobile ===== */}
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-2 md:hidden">
        <Link to="/" onClick={() => setOpen(false)} className="flex items-center" aria-label="El Pajar de Benito · Inicio">
          <img
            src={logoAsset.url}
            alt="El Pajar de Benito · Casa Rural"
            width={scrolled ? 52 : 96}
            height={scrolled ? 52 : 96}
            className={`object-contain transition-all duration-300 ${scrolled ? "h-[52px] w-[52px]" : "h-24 w-24"}`}
          />
        </Link>
        <button
          aria-label="Abrir menú"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <nav className="border-t border-border/60 bg-background md:hidden">
          <div className="mx-auto flex max-w-6xl flex-col px-4 py-2">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="border-b border-border/40 py-3 text-sm text-foreground/85"
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/reserva"
              onClick={() => setOpen(false)}
              className="my-3 rounded-full bg-primary px-5 py-3 text-center text-sm font-medium text-primary-foreground"
            >
              Reservar
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}